// People: who can sign in to this league's admin, and in what role; and the
// staff invites that bring new people in (registration plan S2).
//
//   list     GET …/admins
//   invite   POST /platform/leagues/:id/invites { email, role } (confirm,
//            since it emails) → the send outcome, with the server's link to
//            copy when the email didn't go out
//   pending  GET /platform/leagues/:id/invites, with Resend, Copy link and
//            Revoke (POST /platform/invites/:inviteId/…)
//   role     PATCH …/admins/:userId { role }, per row, rolls back on error
//   password PUT …/admins/:userId/password { password }, from a dialog: rescues
//            an account that can't sign in (#621)
//   remove   DELETE …/admins/:userId, with confirm
//
// People are invited by email and set their own password (or sign in with an
// account they already have); nobody types a first password for them here.
// Set password is only the rescue for an existing member. Every write reloads
// the tenant (reload() refetches every ['tenant', id, …] key: the roster,
// admin_count and the pending invites).

import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import type { InviteSendResult, LeagueRole, TenantAdmin } from '@/api/types'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CheckIcon,
  ConfirmDialog,
  DataTable,
  EmailText,
  EmptyState,
  ErrorState,
  FormField,
  InlineAlert,
  KeyIcon,
  PlusIcon,
  RelativeTime,
  SaveStatus,
  SelectInput,
  SkeletonRows,
  SpinnerIcon,
  TextInput,
  TrashIcon,
  useSaveAction,
  useToast,
  type Column,
} from '@/components/ui'
import { formatDate } from '@/lib/format'
import { useQuery } from '@/lib/query'
import { useTenant } from '../tenantContext'
import { InviteResult, PendingInvitesCard } from '../../invites/StaffInvites'

const ROLES: Array<{ value: LeagueRole; label: string; description: string }> = [
  { value: 'admin', label: 'Admin', description: 'Everything in the league admin, including settings and people.' },
  { value: 'editor', label: 'Editor', description: 'Content, schedules, teams and stats; no settings or people.' },
  { value: 'stat-keeper', label: 'Stat keeper', description: 'Scores and stats for games only.' },
]
const ROLE_OPTIONS = ROLES.map((r) => ({ value: r.value, label: r.label }))
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function PeopleTab() {
  const { tenant, reload } = useTenant()
  const toast = useToast()
  const key = ['tenant', tenant.id, 'admins'] as const
  const { data, error, status, refetch } = useQuery(key, (signal) => api.tenants.admins.list(tenant.id, signal))
  const [removing, setRemoving] = useState<TenantAdmin | null>(null)
  const [settingPassword, setSettingPassword] = useState<TenantAdmin | null>(null)
  const [newPassword, setNewPassword] = useState('')

  // reload() refetches everything under 'tenant' (this roster included).
  const afterWrite = () => reload()

  const admins = data?.admins ?? []

  const columns: Column<TenantAdmin>[] = [
    { id: 'person', header: 'Person', primary: true, cell: (a) => <PersonCell admin={a} />, className: 'min-w-[11rem]' },
    { id: 'role', header: 'Role', cell: (a) => <RoleCell admin={a} tenantId={tenant.id} onSaved={afterWrite} />, className: 'w-44' },
    // Below lg (a 486–538px card) five columns don't fit (#991): Last login and Added move
    // under the person. In the table (md+) the row actions are icon buttons: with labels,
    // Set password and Remove push it past its 697px card at 1440 (#988, #621). Their
    // aria-labels name the person and the title shows the action on hover. Phone cards
    // have the room, so they keep the labels.
    { id: 'login', header: 'Last login', cell: (a) => <RelativeTime value={a.last_login_at} staleAfterDays={30} />, className: 'max-lg:hidden' },
    { id: 'added', header: 'Added', cell: (a) => <span className="whitespace-nowrap text-muted tabular">{formatDate(a.added_at)}</span>, hideOnMobile: true, className: 'max-lg:hidden' },
    {
      id: 'remove',
      header: <span className="sr-only">Actions</span>,
      mobileLabel: ' ',
      align: 'right',
      cell: (a) => (
        <span className="inline-flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            icon={<KeyIcon size={14} />}
            onClick={() => {
              setNewPassword('')
              setSettingPassword(a)
            }}
            aria-label={`Set password for ${a.email}`}
            title="Set password"
          >
            <span className="md:sr-only">Set password</span>
          </Button>
          <Button size="sm" variant="ghost" icon={<TrashIcon size={14} />} onClick={() => setRemoving(a)} aria-label={`Remove ${a.email}`} title="Remove">
            <span className="md:sr-only">Remove</span>
          </Button>
        </span>
      ),
    },
  ]

  return (
    <div className="grid items-start gap-4 min-[1400px]:grid-cols-[1.6fr_1fr]">
      <Card flush className="overflow-hidden">
        <div className="px-4 pt-4 sm:px-5">
          <CardHeader title="League admins" description="People who can sign in to this league's admin. Changes apply at their next request." />
        </div>
        {status === 'loading' && !data ? (
          <SkeletonRows rows={3} label="Loading admins" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load the admins" onRetry={refetch} />
        ) : admins.length === 0 ? (
          <EmptyState title="No league admins yet" description="Nobody can sign in to this league's admin. Add someone by email." />
        ) : (
          <div className="border-t border-line">
            <DataTable caption="League admins" rows={admins} columns={columns} rowKey={(a) => a.user_id} />
          </div>
        )}
      </Card>

      <div className="grid gap-4">
        <InviteCard tenantId={tenant.id} tenantName={tenant.name} onSent={afterWrite} />
        <PendingInvitesCard
          title="Pending invites"
          queryKey={['tenant', tenant.id, 'invites']}
          load={(signal) => api.invites.listForTenant(tenant.id, signal)}
          showRole
        />
      </div>

      <ConfirmDialog
        open={!!settingPassword}
        onClose={() => setSettingPassword(null)}
        title={`Set ${settingPassword?.name || settingPassword?.email || ''}'s password`}
        description={
          <>
            They sign in with it from now on, in every league their account belongs to. No email is sent, so share it with them yourself.
          </>
        }
        tone="primary"
        confirmLabel="Set password"
        onConfirm={async () => {
          if (newPassword.length < 8) throw new Error('Use at least 8 characters.')
          const who = settingPassword!
          await api.tenants.admins.setPassword(tenant.id, who.user_id, newPassword)
          setNewPassword('')
          toast.success(`${who.email}'s password was set.`)
        }}
      >
        <FormField label="New password" required hint="At least 8 characters.">
          {({ id, describedBy }) => (
            <TextInput id={id} type="password" autoComplete="new-password" aria-describedby={describedBy} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          )}
        </FormField>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.name || removing?.email || ''}?`}
        description={
          <>
            They lose access to {tenant.name}&apos;s admin straight away. Their account stays, along with any other leagues they belong to.
          </>
        }
        confirmLabel="Remove from league"
        onConfirm={async () => {
          const who = removing!
          await api.tenants.admins.remove(tenant.id, who.user_id)
          afterWrite()
          toast.success(`${who.email} was removed from ${tenant.name}.`)
        }}
      />
    </div>
  )
}

function PersonCell({ admin }: { admin: TenantAdmin }) {
  return (
    <span className="grid min-w-0 gap-0.5">
      <span className="flex flex-wrap items-center gap-1.5">
        <Link to={`/users/${admin.user_id}`} className="truncate font-semibold text-ink hover:text-accent">
          {admin.name || admin.email}
        </Link>
        {!admin.is_active ? <Badge tone="bad">Deactivated</Badge> : null}
      </span>
      {admin.name ? (
        <span className="break-words text-xs text-muted [overflow-wrap:anywhere]">
          <EmailText email={admin.email} />
        </span>
      ) : null}
      {/* Last login and Added, where those columns are hidden (md–lg, #991). */}
      <span className="hidden flex-wrap gap-x-1 text-xs text-muted md:max-lg:flex">
        <span className="whitespace-nowrap">
          Last login <RelativeTime value={admin.last_login_at} staleAfterDays={30} />
        </span>
        <span aria-hidden>·</span>
        <span className="whitespace-nowrap tabular">Added {formatDate(admin.added_at)}</span>
      </span>
    </span>
  )
}

function RoleCell({ admin, tenantId, onSaved }: { admin: TenantAdmin; tenantId: number; onSaved: () => void }) {
  const [value, setValue] = useState<LeagueRole>(admin.role)
  const [attempt, setAttempt] = useState<LeagueRole>(admin.role)
  const save = useSaveAction()
  // Follow the server when the roster refetches.
  useEffect(() => setValue(admin.role), [admin.role])

  const commit = async (next: LeagueRole) => {
    setAttempt(next)
    setValue(next)
    const ok = await save.run(() => api.tenants.admins.changeRole(tenantId, admin.user_id, next))
    if (ok) onSaved()
    else setValue(admin.role)
  }

  // Saving… / Saved show as an icon in a slot beside the select (always reserved, so
  // nothing shifts), not as a line under it: that line kept the select ~11px above
  // the row's centre (#960). An error still gets the full line, with Retry.
  const error = save.status === 'error'
  const word = save.status === 'saving' ? 'Saving…' : save.status === 'saved' ? 'Saved' : ''
  return (
    <div className={error ? 'grid gap-1' : 'flex items-center gap-1.5'}>
      <SelectInput
        aria-label={`Role for ${admin.email}`}
        value={value}
        options={ROLE_OPTIONS}
        disabled={save.status === 'saving'}
        invalid={save.status === 'error'}
        onChange={(e) => commit(e.target.value as LeagueRole)}
        // 8rem fits "Stat keeper" plus the chevron; the 16px status slot beside it (#967)
        // then costs nothing, and the table fits its 697px card again (#988).
        className="h-8 min-w-[8rem]"
      />
      {error ? (
        <SaveStatus state={save} onRetry={() => commit(attempt)} />
      ) : (
        <span role="status" aria-live="polite" title={word || undefined} className="inline-grid w-4 flex-none place-items-center">
          {save.status === 'saving' && <SpinnerIcon size={13} className="text-muted" />}
          {save.status === 'saved' && <CheckIcon size={13} className="text-good" />}
          <span className="sr-only">{word}</span>
        </span>
      )}
    </div>
  )
}

function InviteCard({ tenantId, tenantName, onSent }: { tenantId: number; tenantName: string; onSent: () => void }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<LeagueRole>('admin')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<InviteSendResult | null>(null)
  const [problem, setProblem] = useState<{ tone: 'bad' | 'warn'; text: string; inviteId?: number; inviteEmail?: string; inactiveEmail?: string } | null>(null)
  // The pending invite the alert's "Resend it" asks about: resending emails
  // someone, so it goes through a confirm like every other send.
  const [resending, setResending] = useState<{ inviteId: number; email: string } | null>(null)

  const clean = email.trim().toLowerCase()
  const roleLabel = ROLES.find((r) => r.value === role)?.label ?? role
  const roleHint = ROLES.find((r) => r.value === role)?.description

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!EMAIL_RE.test(clean)) {
      setEmailError('Enter a valid email address.')
      return
    }
    setEmailError(null)
    setProblem(null)
    setResult(null)
    setConfirming(true)
  }

  const send = async () => {
    try {
      const r = await api.invites.inviteToTenant(tenantId, { email: clean, role })
      setResult(r)
      setEmail('')
      onSent()
    } catch (err) {
      if (isApiError(err) && err.field === 'email') setEmailError(err.message)
      else if (isApiError(err) && err.code === 'invite_pending') {
        const id = Number(err.details?.invite_id)
        setProblem({ tone: 'warn', text: err.message, inviteId: Number.isInteger(id) && id > 0 ? id : undefined, inviteEmail: clean })
      } else if (isApiError(err) && err.code === 'account_inactive') setProblem({ tone: 'bad', text: err.message, inactiveEmail: clean })
      else setProblem({ tone: 'bad', text: errorMessage(err) })
      onSent()
    }
  }

  const resend = async (inviteId: number) => {
    try {
      setResult(await api.invites.resendInvite(inviteId))
      setProblem(null)
      setEmail('')
    } catch (err) {
      setProblem({ tone: 'bad', text: errorMessage(err) })
    } finally {
      onSent()
    }
  }

  return (
    <Card>
      <CardHeader title="Invite someone" description="By email. They create an account, or sign in with one they have, and join as the role you pick." />
      <form className="grid gap-4" onSubmit={submit} noValidate>
        <FormField label="Email" required error={emailError}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="email"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              aria-describedby={describedBy}
              invalid={invalid}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setEmailError(null)
              }}
              placeholder="coach@example.com"
            />
          )}
        </FormField>
        <FormField label="Role" hint={roleHint}>
          {({ id, describedBy }) => (
            <SelectInput id={id} aria-describedby={describedBy} value={role} options={ROLE_OPTIONS} onChange={(e) => setRole(e.target.value as LeagueRole)} />
          )}
        </FormField>
        {result ? <InviteResult result={result} className="break-words" /> : null}
        {problem ? (
          <InlineAlert
            tone={problem.tone}
            className="break-words"
            action={
              problem.inviteId ? (
                <Button size="sm" onClick={() => setResending({ inviteId: problem.inviteId!, email: problem.inviteEmail ?? clean })}>
                  Resend it
                </Button>
              ) : problem.inactiveEmail ? (
                <Link to={`/users?q=${encodeURIComponent(problem.inactiveEmail)}`} className="text-xs font-semibold text-accent hover:underline">
                  Find the account
                </Link>
              ) : undefined
            }
          >
            {problem.text}
          </InlineAlert>
        ) : null}
        <div className="flex justify-end">
          <Button type="submit" variant="primary" icon={<PlusIcon size={16} />}>
            Send invite
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        tone="primary"
        title={`Send an invite to ${clean}?`}
        description={`They get an email with a link to join ${tenantName} as ${roleLabel}.`}
        confirmLabel="Send invite"
        onConfirm={send}
      />
      <ConfirmDialog
        open={!!resending}
        onClose={() => setResending(null)}
        tone="primary"
        title={`Resend the invite to ${resending?.email ?? ''}?`}
        description="They get the same link again. It still expires when it was going to."
        confirmLabel="Resend invite"
        onConfirm={() => resend(resending!.inviteId)}
      />
    </Card>
  )
}
